// src/components/conduction-social/ActivityFeedPost.tsx
import ProjectVisual from "@/components/conduction-social/ProjectVisual";
import { AuthContext } from "@/context/AuthContext";
import {
	Project
} from "@/javascript/project-objects";
import {
	Box,
	Button,
	Card,
	Group,
	rgba,
	Stack,
	Text,
	Title,
	useMantineTheme
} from "@mantine/core";
import React, {
	memo,
	useContext,
	useEffect,
	useState
} from "react";

import { useDeviceLayout } from "@/hooks/useDeviceLayout";
import { User } from "@/javascript/user-objects";
import { Utils } from "@/javascript/utils";

/* -------------------------- shared helpers -------------------------- */

function formatRelativeWithLabel(
	date?: Date | null,
	label: "Submitted" | "Published" | "Posted" = "Submitted"
): string {
	if (!date) return "";
	const now = Date.now();
	const diffSec = Math.max(0, Math.floor((now - date.getTime()) / 1000));
	if (diffSec < 60) return `${label} just now`;
	const min = Math.floor(diffSec / 60);
	if (min < 60) return `${label} ${min} minute${min === 1 ? "" : "s"} ago`;
	const hrs = Math.floor(min / 60);
	if (hrs < 24) return `${label} ${hrs} hour${hrs === 1 ? "" : "s"} ago`;
	const days = Math.floor(hrs / 24);
	if (days < 7) return `${label} ${days} day${days === 1 ? "" : "s"} ago`;
	const weeks = Math.floor(days / 7);
	if (weeks < 5) return `${label} ${weeks} week${weeks === 1 ? "" : "s"} ago`;
	const months = Math.floor(days / 30);
	if (months < 12) return `${label} ${months} month${months === 1 ? "" : "s"} ago`;
	const years = Math.floor(days / 365);
	return `${label} ${years} year${years === 1 ? "" : "s"} ago`;
}

/* -------------------------- shared header --------------------------- */

type PostHeaderProps = {
	owner: User | null | undefined;
	headline: string;
	rightTimeLabel: string;
};

const MockPostHeader: React.FC<PostHeaderProps> = memo(({ owner, headline, rightTimeLabel }) => {
	const deviceLayout = useDeviceLayout();
	const theme = useMantineTheme();
	const [avatar, setAvatar] = useState<JSX.Element | null>(null);
	const session = useContext(AuthContext);
	const baseColorName = Utils.getColorFromString(owner?.id ?? "unknown");

	if (!session) throw new Error("PostHeader must be a child of SessionContext.");

	useEffect(() => {
		let cancelled = false;
		if (owner) {
			session.fetchProfileAvatar(owner).then((av) => { if (!cancelled) setAvatar(av); });
		} else {
			setAvatar(null);
		}
		return () => { cancelled = true; };
	}, [owner, session]);

	const ownerName = owner?.username ?? "Unknown user";

	const pastelBg = rgba(theme.colors[baseColorName][5], 0.05); // mid shade, light alpha
	const pastelBorder = rgba(theme.colors[baseColorName][5], 0.15); // mid shade, medium alpha

	return (
		<Box
			// don't pass 'bg' with just the color name; set a custom pastel BG via style
			style={{
				backgroundColor: pastelBg,
				border: `1px solid ${pastelBorder}`,
			}}
			w="100%"
			p="sm"
		>
			<Group justify="space-between" align="center">
				<Group gap="xs">
					<Button
						leftSection={avatar}
						variant="subtle"
						size="md"
						p={2}
					>
						<Text fw={700} fz={deviceLayout.isPhoneOrSmaller ? "sm" : "md"} c={theme.colors.blue[8]}>
							{ownerName}
						</Text>
					</Button>
					{!deviceLayout.isPhoneOrSmaller && (
						<Text fw={700} fz={deviceLayout.isPhoneOrSmaller ? "sm" : "md"} c={theme.colors.blue[8]}>
							{headline}
						</Text>
					)}
				</Group>
				<Text fz="xs" c="dimmed">
					{rightTimeLabel}
				</Text>
			</Group>
		</Box>
	);
});
MockPostHeader.displayName = "PostHeader";

/* -------------------------- publication post ------------------------ */

type PublicationPostProps = { project: Project };

const MockPublicationPost: React.FC<PublicationPostProps> = ({ project }) => {
	const theme = useMantineTheme();
	const deviceLayout = useDeviceLayout();
	const CARD_PADDING = deviceLayout.isPhoneOrSmaller ? 8 : 20;
	const MEDIA_HEIGHT = deviceLayout.isPhoneOrSmaller ? 220 : 340;

	return (
		<Card.Section p={CARD_PADDING} h="100%">
			<Stack h="100%">
				<Title order={3} c={theme.colors.blue[7]}>
					{project.getName() ?? "Untitled project"}
				</Title>

				<Box
					style={{
						position: "relative",
						borderRadius: "var(--mantine-radius-md)",
						overflow: "hidden",
						background: theme.colors.gray[0],
					}}
					h="100%"
				>
					{project && (
						<ProjectVisual
							project={project}
							beatsPerMeasure={4}
							rowHeight={Math.min(60, Math.max(24, (MEDIA_HEIGHT - 60) / Math.max(1, project.tracks.length)))}
							pxPerMeasure={10}
							showMeasureNumbers
							showPlayhead
						/>
					)}
				</Box>
			</Stack>
		</Card.Section>
	);
};

/* -------------------------- router component ------------------------ */

type MockActivityFeedPostProps = { project: Project; }

const MockActivityFeedPost: React.FC<MockActivityFeedPostProps> = (props) => {
	const deviceLayout = useDeviceLayout();
	const CARD_MAX_WIDTH = deviceLayout.isPhoneOrSmaller ? "auto" : 1080;
	const CARD_MIN_HEIGHT = deviceLayout.isPhoneOrSmaller ? "auto" : 450;

	const owner = props.project.getUser();;
	const headline = "shared this project!";
	const rightTimeLabel = formatRelativeWithLabel(new Date(), "Published");

	return (
		<Card
			withBorder
			radius="sm"
			p="md"
			bg="#F6F8FB"
			w="100%"
			h="100%"
			maw={CARD_MAX_WIDTH}
			mih={CARD_MIN_HEIGHT}
		>
			<Card.Section>
				<MockPostHeader owner={owner} headline={headline} rightTimeLabel={rightTimeLabel} />
			</Card.Section>
			<MockPublicationPost project={props.project} />
		</Card>
	);
};

export default MockActivityFeedPost;
